<template>
  <div class="instructions-language-picker">
    <div class="picker-wrapper">
      <ExSelectPicker
        ref="selectPickerRef"
        pickerLabel=""
        :label="label"
        searchLabel=""
        :items="processedLanguages"
        :value="modelValue"
        :useCode="false"
        :isSelectedItemRemove="isSelectedItemRemove"
        :isSearch="false"
        @item-selected="handleItemSelected"
        @clear-selection="clearItemSelected"
        :isforceReadonly="isforceReadonly"
        :isShowDefautValue="isShowDefautValue"
        :defaultCurrencyProp="isShowDefautValue ? 'en' : (defaultLanguage?.code || '')"
        placeholder="Select language"
        noRecordMessage="No language found"
        @input-cleared="clearItemSelected"
      />
      <!-- Overlay: single click target; stop propagation so ExSelectPicker's handleClickOutside doesn't see this click and close the list -->
      <div
        class="picker-overlay"
        aria-hidden="true"
        @click.stop="toggleDropdown"
      />
    </div>
    <v-icon v-if="!isDropdownOpen" class="chevron-icon">mdi-chevron-down</v-icon>
  </div>
</template>

<script setup>
import ExSelectPicker from "@components/SelectPicker/ExSelectPicker.vue";
import { defineProps, defineEmits, computed, ref, onMounted, onBeforeUnmount, watch } from "vue";

const props = defineProps({
  modelValue: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  items: {
    required: false,
    type: Array,
    default() {
      return []
    }
  },
  pickerLabel: {
    type: String,
    required: false,
    default: "",
  },
  label: {
    type: String,
    required: false,
    default: "English",
  },
  searchLabel: {
    type: String,
    required: false,
    default: "",
  },
  useCode: {
    type: Boolean,
    required: false,
    default: false,
  },
  isSelectedItemRemove: {
    type: Boolean,
    required: false,
    default: false,
  },
  isforceReadonly: {
    type: Boolean,
    default: false,
  },
  isShowDefautValue: {
    type: Boolean,
    default: true,
  },
  pickerPopUp: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue", "item-language-selected", "clear-selection"]);

// Default languages list (English and Spanish only)
const defaultLanguages = [
  { id: 1, name: "English", code: "en" },
  { id: 2, name: "Spanish", code: "es" },
];

const defaultLanguage = ref(null);
const selectPickerRef = ref(null);
const isDropdownOpen = ref(false);

// Toggle dropdown: focus to open; close by running ExSelectPicker's outside-click logic (onInputBlur does not set isListVisible = false)
const toggleDropdown = () => {
  if (!selectPickerRef.value?.$el) return;
  const input = selectPickerRef.value.$el.querySelector('input');
  if (!input) return;
  const isOpen = !!selectPickerRef.value.isListVisible;
  if (isOpen) {
    // ExSelectPicker only closes list in handleClickOutside / onAppendIconClick, not in onInputBlur. Trigger close.
    if (selectPickerRef.value.handleClickOutside) {
      selectPickerRef.value.handleClickOutside({ target: document.body });
    } else {
      selectPickerRef.value.isListVisible = false;
      selectPickerRef.value.isInputFocused = false;
      input.blur();
    }
  } else {
    input.focus();
  }
};

// Handle backspace to clear selection - readonly inputs still fire keydown events
// Also track dropdown state to show/hide chevron and add click handler for entire container
onMounted(() => {
  // Wait for ExSelectPicker to mount
  setTimeout(() => {
    if (selectPickerRef.value && selectPickerRef.value.$el) {
      const input = selectPickerRef.value.$el.querySelector('input');
      if (input) {
        // Add keydown listener for backspace - works even on readonly inputs
        const handleKeyDown = (e) => {
          if (e.key === 'Backspace' && props.modelValue && Object.keys(props.modelValue).length > 0) {
            e.preventDefault();
            e.stopPropagation();
            // Clear the selection
            if (selectPickerRef.value && selectPickerRef.value.resetSelectedItem) {
              selectPickerRef.value.resetSelectedItem();
            }
            clearItemSelected();
            handleItemSelected(null);
          }
        };
        
        input.addEventListener('keydown', handleKeyDown, true);
        
        // Prevent language label from staying highlighted when input gets focus (e.g. after opening dropdown)
        const clearSelectionOnFocus = () => {
          requestAnimationFrame(() => {
            if (input && document.activeElement === input) {
              const len = (input.value || '').length;
              input.setSelectionRange(len, len);
            }
          });
        };
        input.addEventListener('focus', clearSelectionOnFocus);
        selectPickerRef.value._backspaceHandler = handleKeyDown;
        selectPickerRef.value._clearSelectionOnFocus = clearSelectionOnFocus;
      }
      
      // Click is handled by the overlay in template (single, consistent toggle)

      // Update chevron visibility when dropdown state changes
      const checkDropdownState = () => {
        if (selectPickerRef.value && selectPickerRef.value.$el) {
          isDropdownOpen.value = !!selectPickerRef.value.isListVisible;
        }
      };
      checkDropdownState();
      const dropdownCheckInterval = setInterval(checkDropdownState, 100);
      selectPickerRef.value._dropdownCheckInterval = dropdownCheckInterval;
    }
  }, 100);
});

onBeforeUnmount(() => {
  if (selectPickerRef.value && selectPickerRef.value.$el) {
    const input = selectPickerRef.value.$el.querySelector('input');
    if (input) {
      if (selectPickerRef.value._backspaceHandler) {
        input.removeEventListener('keydown', selectPickerRef.value._backspaceHandler, true);
      }
      if (selectPickerRef.value._clearSelectionOnFocus) {
        input.removeEventListener('focus', selectPickerRef.value._clearSelectionOnFocus);
      }
    }
    if (selectPickerRef.value._dropdownCheckInterval) {
      clearInterval(selectPickerRef.value._dropdownCheckInterval);
    }
  }
});

const processedLanguages = computed(() => {
  // Use items prop if it has values, otherwise use default languages
  const languageSource = props.items.length > 0 ? props.items : defaultLanguages;
  
  return languageSource.map(language => {
    // Set default language (English) if no selection and isShowDefautValue is true
    if (props.isShowDefautValue && !defaultLanguage.value && language.code === 'en') {
      defaultLanguage.value = language;
    }
    
    // Check if this language exists in items prop with disabled=true
    const disabledItem = props.items.find(item => 
      item.id === language.id && item.disabled === true
    );

    return {
      ...language,
      disabled: disabledItem ? true : language.disabled,
    };
  });
});

const handleItemSelected = (selectedItem) => {
  emit("update:modelValue", selectedItem);
  emit("item-language-selected", selectedItem);
};

const clearItemSelected = () => {
  if(props.pickerPopUp){
    handleItemSelected(null);
  }
  emit("clear-selection");
};
</script>

<style scoped>
/* Layout: Vertical flow, Fixed 133×45px, Top 20px, Left 20px, Gap spacing-md */
.instructions-language-picker {
  display: flex;
  flex-direction: column;
  width: 133px;
  height: 45px;
  margin-top: 20px;
  margin-left: 20px;
  gap: var(--spacing-md, 8px);
  position: relative;
}
.picker-wrapper {
  position: relative;
  width: 133px;
  height: 45px;
  flex-shrink: 0;
}
/* Overlay: single click target for consistent open/close (no flash, no double-handling) */
.picker-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 10;
  cursor: pointer;
  pointer-events: auto;
}
.instructions-language-picker :deep(.select-picker),
.instructions-language-picker :deep(.select-picker.v-container) {
  width: 133px !important;
  max-width: 133px !important;
  min-width: 133px !important;
  padding: 0 !important;
}
.instructions-language-picker :deep(.custom-response-picker) {
  width: 133px !important;
  max-width: 133px !important;
  min-width: 133px !important;
  min-height: 45px !important;
  height: 45px !important;
}
.instructions-language-picker :deep(.custom-response-picker .v-input__slot fieldset) {
  min-height: 45px !important;
  height: 45px !important;
}

/* Hide the label above the picker container (e.g. "Search Language*") and remove its gap */
.instructions-language-picker :deep(.label-name) {
  display: none !important;
  margin: 0 !important;
  padding: 0 !important;
  height: 0 !important;
  min-height: 0 !important;
  overflow: hidden !important;
}
/* Remove gap above the picker field (fill the space where the label was) */
.instructions-language-picker :deep(.select-picker),
.instructions-language-picker :deep(.select-picker.v-container) {
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  margin-top: 0 !important;
}
.instructions-language-picker :deep(.select-picker > .v-input),
.instructions-language-picker :deep(.custom-response-picker) {
  margin-top: 0 !important;
}
/* Override ExSelectPicker's .custom-response-picker margin-top: 8px that creates the gap */
.instructions-language-picker :deep(.select-picker .custom-response-picker) {
  margin-top: 0 !important;
}
/* Hide the floating label that appears on focus (e.g. "Search Language") */
.instructions-language-picker :deep(.response-label-class),
.instructions-language-picker :deep(.custom-label-class) {
  display: none !important;
}
/* Remove the gap in the border (fieldset legend notch) so the outline is continuous */
.instructions-language-picker :deep(.custom-response-picker fieldset legend),
.instructions-language-picker :deep(.custom-response-picker .v-input__slot fieldset legend) {
  width: 0 !important;
  min-width: 0 !important;
  max-width: 0 !important;
  height: 0 !important;
  padding: 0 !important;
  margin: 0 !important;
  overflow: hidden !important;
  line-height: 0 !important;
}
.instructions-language-picker :deep(.custom-response-picker fieldset legend span),
.instructions-language-picker :deep(.custom-response-picker .v-input__slot fieldset legend span) {
  display: inline-block;
  width: 0 !important;
  min-width: 0 !important;
  max-width: 0 !important;
  overflow: hidden !important;
  padding: 0 !important;
}

::v-deep(.selected-currency-icon img) {
  height: 100% !important;
  width: 100% !important;
  object-fit: cover !important;
  margin-left: 0px !important;
  z-index: 1;
}

::v-deep(.v-avatar) {
  overflow: none !important;
}

/* Override readonly behavior: allow keydown (backspace) but prevent click focus/selection on label */
.instructions-language-picker :deep(.custom-response-picker.v-input--is-readonly > .v-input__control > .v-input__slot > input),
.instructions-language-picker :deep(.custom-response-picker input[readonly]) {
  /* Clicks go to overlay; keep pointer-events so programmatic focus and keydown work */
  cursor: pointer !important;
  user-select: none !important;
  -webkit-user-select: none !important;
  -moz-user-select: none !important;
  -ms-user-select: none !important;
  caret-color: transparent !important;
}
/* Prevent language name from appearing selected/highlighted */
.instructions-language-picker :deep(.custom-response-picker .v-input__slot input)::selection,
.instructions-language-picker :deep(.custom-response-picker input)::selection {
  background: transparent !important;
  color: inherit !important;
}
/* Hide search icon since search is disabled */
.instructions-language-picker :deep(.prepend-icon-inner-custom-search) {
  display: none !important;
}

/* Label inside language picker container: Text md/Medium, Hug 56×24px, right-aligned with consistent right padding; no margins on input */
.instructions-language-picker :deep(.custom-response-picker .v-input__slot input),
.instructions-language-picker :deep(.custom-response-picker input) {
  font-family: Inter, sans-serif !important;
  font-weight: 500 !important;
  font-size: 16px !important;
  line-height: 24px !important;
  letter-spacing: 0% !important;
  /* text-align: left !important; */
  padding-left: var(--spacing-md, 0px) !important;
  /* Hug content: width/height from content; min dimensions from spec */
  min-height: 24px !important;
  height: 24px !important;
  box-sizing: border-box;
  margin-left: 3px !important;
}
.instructions-language-picker :deep(.custom-response-picker .v-input__control .v-input__slot) {
  padding-top: 0;
  padding-bottom: 0;
  align-items: left !important;
}

/* Dropdown chevron: Fixed 20×20px */
.instructions-language-picker :deep(.custom-response-picker .v-input__append-inner),
.instructions-language-picker :deep(.custom-response-picker .append-icon-hover) {
  width: 20px !important;
  height: 20px !important;
  min-width: 20px !important;
  min-height: 20px !important;
}
.instructions-language-picker :deep(.custom-response-picker .v-input__append-inner .v-icon),
.instructions-language-picker :deep(.custom-response-picker .append-icon-hover) {
  font-size: 20px !important;
  width: 20px !important;
  height: 20px !important;
  min-width: 20px !important;
  min-height: 20px !important;
}

/* Dropdown menu: above overlay (z-index 15) so list is clickable when open */
.instructions-language-picker :deep(.list-picker-container) {
  width: 133px !important;
  min-width: 133px !important;
  max-width: 133px !important;
  min-height: 45px !important;
  height: auto !important;
  z-index: 15 !important;
  position: relative;
}
.instructions-language-picker :deep(.list-picker-container .res-custom-list-picker) {
  width: 133px !important;
  min-width: 133px !important;
  max-width: 133px !important;
}
.instructions-language-picker :deep(.list-picker-container .res-v-list-item) {
  min-width: 0 !important;
  width: 100% !important;
  box-sizing: border-box !important;
}

/* Additional chevron icon for collapsed state */
.instructions-language-picker {
  position: relative;
}
.instructions-language-picker .chevron-icon {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 20px !important;
  width: 20px !important;
  height: 20px !important;
  pointer-events: none;
  z-index: 1;
}
</style>